import { useEffect, useMemo, useState } from 'react';

type SocialGenerationMode = 'post' | 'daily';

interface SocialGenerationProgressProps {
  mode: SocialGenerationMode;
  platform: string;
  quantity?: string;
}

const POST_STAGES = [
  {
    threshold: 0,
    label: 'Reading identity',
    detail: 'Using the account type, audience, tone, and platform context.',
    icon: 'ri-user-settings-line',
  },
  {
    threshold: 20,
    label: 'Finding the angle',
    detail: 'Choosing a hook pattern, content type, and creative direction.',
    icon: 'ri-compass-3-line',
  },
  {
    threshold: 42,
    label: 'Writing captions',
    detail: 'Drafting distinct caption options, hooks, CTA, and hashtags.',
    icon: 'ri-chat-quote-line',
  },
  {
    threshold: 68,
    label: 'Shaping visuals',
    detail: 'Preparing the image concept or short-form video idea.',
    icon: 'ri-image-line',
  },
  {
    threshold: 86,
    label: 'Final polish',
    detail: 'Checking platform fit, variety, and publish-ready structure.',
    icon: 'ri-check-double-line',
  },
];

const DAILY_STAGES = [
  {
    threshold: 0,
    label: 'Mapping identity',
    detail: 'Reading audience, account type, content style, and goals.',
    icon: 'ri-user-settings-line',
  },
  {
    threshold: 18,
    label: 'Planning variety',
    detail: 'Choosing post types, angles, patterns, and daily cadence.',
    icon: 'ri-calendar-check-line',
  },
  {
    threshold: 40,
    label: 'Writing posts',
    detail: 'Drafting hooks, captions, CTAs, and hashtags for each post.',
    icon: 'ri-quill-pen-line',
  },
  {
    threshold: 70,
    label: 'Adding media ideas',
    detail: 'Creating image directions and short-form video ideas.',
    icon: 'ri-video-line',
  },
  {
    threshold: 88,
    label: 'Final review',
    detail: 'Balancing tone, repetition, platform fit, and usefulness.',
    icon: 'ri-check-double-line',
  },
];

export default function SocialGenerationProgress({
  mode,
  platform,
  quantity = '1',
}: SocialGenerationProgressProps) {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const isDaily = mode === 'daily';
  const stages = isDaily ? DAILY_STAGES : POST_STAGES;
  const accent = isDaily
    ? {
        icon: 'ri-calendar-2-line',
        gradient: 'from-emerald-500 to-teal-400',
        dot: 'bg-emerald-400',
        bar: 'from-emerald-500 via-teal-400 to-orange-400',
        activeBorder: 'border-emerald-200',
        activeBg: 'bg-emerald-50',
        activeText: 'text-emerald-700',
        softBorder: 'border-emerald-100',
        softBg: 'bg-emerald-50',
        iconText: 'text-emerald-600',
      }
    : {
        icon: 'ri-sparkling-2-fill',
        gradient: 'from-orange-500 to-amber-400',
        dot: 'bg-orange-400',
        bar: 'from-orange-500 via-amber-400 to-teal-400',
        activeBorder: 'border-orange-200',
        activeBg: 'bg-orange-50',
        activeText: 'text-orange-700',
        softBorder: 'border-orange-100',
        softBg: 'bg-orange-50',
        iconText: 'text-orange-500',
      };

  const estimatedSeconds = useMemo(() => {
    if (!isDaily) return 28;
    if (quantity === '7') return 44;
    if (quantity === '3') return 34;
    return 24;
  }, [isDaily, quantity]);

  useEffect(() => {
    setElapsedSeconds(0);
    const timer = window.setInterval(() => {
      setElapsedSeconds((seconds) => seconds + 1);
    }, 1000);
    return () => window.clearInterval(timer);
  }, [estimatedSeconds, mode, platform, quantity]);

  const progress = Math.min(94, Math.round((elapsedSeconds / estimatedSeconds) * 90) + 4);
  const activeStageIndex = stages.reduce(
    (activeIndex, stage, index) => (progress >= stage.threshold ? index : activeIndex),
    0,
  );
  const activeStage = stages[activeStageIndex];
  const remainingSeconds = Math.max(0, estimatedSeconds - elapsedSeconds);
  const postCount = quantity === '7' ? 7 : quantity === '3' ? 3 : 1;
  const draftedCount = Math.min(
    postCount,
    Math.max(0, Math.floor((progress - 35) / Math.max(1, 55 / postCount))),
  );

  return (
    <div className="bg-white rounded-2xl border border-stone-100 h-full min-h-[500px] flex flex-col items-center justify-center px-5 py-8">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`relative w-12 h-12 flex items-center justify-center rounded-2xl bg-gradient-to-br ${accent.gradient} shrink-0`}>
              <i className={`${accent.icon} text-white text-xl`} />
              <span className="absolute -right-1 -bottom-1 w-4 h-4 rounded-full bg-white flex items-center justify-center">
                <span className={`w-2.5 h-2.5 rounded-full ${accent.dot} animate-ping`} />
              </span>
            </div>
            <div className="min-w-0">
              <p className="text-base font-bold text-stone-900">
                {isDaily ? 'Building your content plan' : 'Writing your social post'}
              </p>
              <p className="text-xs text-stone-400 truncate">
                {platform} {isDaily ? `content plan - ${postCount} post${postCount !== 1 ? 's' : ''}` : 'caption, CTA, and visual direction'}
              </p>
            </div>
          </div>
          <div className="text-right shrink-0">
            <p className="text-xl font-black text-stone-900">{progress}%</p>
            <p className="text-xs text-stone-400">{remainingSeconds > 0 ? `~${remainingSeconds}s` : 'Wrapping up'}</p>
          </div>
        </div>

        <div className="h-3 rounded-full bg-stone-100 overflow-hidden border border-stone-200">
          <div
            className={`h-full rounded-full bg-gradient-to-r ${accent.bar} transition-all duration-700 ease-out`}
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className={`mt-5 rounded-xl border ${accent.softBorder} ${accent.softBg} px-4 py-3 flex items-start gap-3`}>
          <div className={`w-8 h-8 flex items-center justify-center rounded-lg bg-white border ${accent.softBorder} shrink-0`}>
            <i className={`${activeStage.icon} ${accent.iconText} text-sm`} />
          </div>
          <div>
            <p className="text-sm font-bold text-stone-900">{activeStage.label}</p>
            <p className={`text-xs ${accent.activeText} mt-0.5 leading-relaxed`}>{activeStage.detail}</p>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {stages.map((stage, index) => {
            const isDone = index < activeStageIndex;
            const isActive = index === activeStageIndex;
            return (
              <div
                key={stage.label}
                className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-xs transition-colors ${
                  isDone
                    ? 'border-teal-100 bg-teal-50 text-teal-700'
                    : isActive
                    ? `${accent.activeBorder} ${accent.activeBg} ${accent.activeText}`
                    : 'border-stone-100 bg-stone-50 text-stone-400'
                }`}
              >
                <i className={`${isDone ? 'ri-check-line' : stage.icon} text-sm shrink-0`} />
                <span className="font-semibold truncate">{stage.label}</span>
              </div>
            );
          })}
        </div>

        {isDaily && postCount > 1 && (
          <div className="mt-4 flex items-center justify-between rounded-xl border border-stone-200 bg-stone-50 px-4 py-3">
            <div>
              <p className="text-xs font-bold text-stone-700">Drafting plan</p>
              <p className="text-xs text-stone-400 mt-0.5">Each post gets its own hook, angle, CTA, and media idea.</p>
            </div>
            <span className="text-xs font-black text-stone-900">
              {draftedCount}/{postCount}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
